// Centralized API utilities for Academy LMS features
import supabase from "../../../../supabaseClient";

// Courses
export const fetchCourses = async () =>
  await supabase.from('academy_courses').select('*');

// Lessons
export const fetchLesson = async (lessonId) => {
  const { data, error } = await supabase
    .from('academy_lessons')
    .select('*')
    .eq('id', lessonId)
    .maybeSingle();
  if (error) throw error;
  return data;
};

// Groups
export const fetchGroups = async () =>
  await supabase.from('academy_groups').select('*');
export const fetchGroupMembers = async (groupId) =>
  await supabase.from('academy_group_members').select('*, profile:profile_id(id, name, email)').eq('group_id', groupId);

// Assessments
export const fetchAssessments = async (groupId) =>
  await supabase.from('academy_assessments').select('*').eq('group_id', groupId);
export const createAssessment = async (assessment) =>
  await supabase.from('academy_assessments').insert([assessment]);

// Submissions
export const fetchSubmissions = async (assessmentId) =>
  await supabase.from('academy_submissions').select('*').eq('assessment_id', assessmentId);
export const submitAssessment = async (submission) =>
  await supabase.from('academy_submissions').insert([submission]);

// Grading
export const gradeSubmission = async (submissionId, updates) =>
  await supabase.from('academy_submissions').update(updates).eq('id', submissionId);

// Forum
export const fetchForumThreads = async ({ courseId, groupId }) => {
  let query = supabase.from('academy_forum_threads').select('*');
  if (courseId) query = query.eq('course_id', courseId);
  if (groupId) query = query.eq('group_id', groupId);
  return await query.order('created_at', { ascending: false });
};
export const fetchForumPosts = async (threadId) =>
  await supabase.from('academy_forum_posts').select('*').eq('thread_id', threadId).order('created_at', { ascending: true });
export const createForumThread = async (thread) =>
  await supabase.from('academy_forum_threads').insert([thread]);
export const createForumPost = async (post) =>
  await supabase.from('academy_forum_posts').insert([post]);
export const reactToPost = async (reaction) =>
  await supabase.from('academy_forum_reactions').insert([reaction]);
export const removeReaction = async (reactionId) =>
  await supabase.from('academy_forum_reactions').delete().eq('id', reactionId);

// Forum Comments (Academy)
export const fetchForumComments = async (postId) =>
  await supabase
    .from('academy_forum_comments')
    .select('*')
    .eq('post_id', postId)
    .order('created_at', { ascending: true });

export const createForumComment = async ({ post_id, author_id, content, parent_comment_id = null }) =>
  await supabase
    .from('academy_forum_comments')
    .insert([{ post_id, author_id, content, parent_comment_id }]);

export const fetchForumCommentReactions = async (commentId) =>
  await supabase
    .from('academy_forum_comment_reactions')
    .select('*')
    .eq('comment_id', commentId)
    .order('created_at', { ascending: true });

export const addForumCommentReaction = async ({ comment_id, user_id, reaction_type }) =>
  await supabase
    .from('academy_forum_comment_reactions')
    .insert([{ comment_id, user_id, reaction_type }]);

export const removeForumCommentReaction = async (comment_id, user_id, reaction_type) =>
  await supabase
    .from('academy_forum_comment_reactions')
    .delete()
    .eq('comment_id', comment_id)
    .eq('user_id', user_id)
    .eq('reaction_type', reaction_type);

// Collaborative Docs
// For document files, use 'academy-documents' bucket
// For forum post media uploads, use 'forum-media' bucket
export const fetchDocs = async ({ lessonId, groupId }) => {
  let query = supabase.from('academy_collab_docs').select('*');
  if (lessonId) query = query.eq('lesson_id', lessonId);
  if (groupId) query = query.eq('group_id', groupId);
  return await query;
};
export const fetchDocVersions = async (docId) =>
  await supabase.from('academy_collab_doc_versions').select('*').eq('doc_id', docId).order('created_at', { ascending: false });
export const fetchDocComments = async (docId) =>
  await supabase.from('academy_collab_doc_comments').select('*').eq('doc_id', docId).order('created_at', { ascending: true });
export const createDoc = async (doc) =>
  await supabase.from('academy_collab_docs').insert([doc]);
export const createDocVersion = async (version) =>
  await supabase.from('academy_collab_doc_versions').insert([version]);
export const createDocComment = async (comment) =>
  await supabase.from('academy_collab_doc_comments').insert([comment]);

// Analytics, Progress, Leaderboard
export const fetchUserAnalytics = async (userId) =>
  await supabase.from('academy_analytics').select('*').eq('user_id', userId);
export const fetchProgress = async (enrollmentId) =>
  await supabase.from('academy_user_progress').select('*').eq('enrollment_id', enrollmentId);
export const updateProgress = async (progressId, updates) =>
  await supabase.from('academy_user_progress').update(updates).eq('id', progressId);
export const updateLessonProgress = async (lessonId, userId, progressPercent, courseId, moduleId) => {
  if (!courseId || !moduleId) {
    // In preview contexts, course/module IDs may be unavailable. Skip updating rather than erroring.
    return { skipped: true };
  }
  const now = new Date().toISOString();
  // Try to find an existing progress row for this user + lesson
  const { data: existing, error: findError } = await supabase
    .from('academy_user_progress')
    .select('*')
    .eq('user_id', userId)
    .eq('course_id', courseId)
    .eq('module_id', moduleId)
    .eq('lesson_id', lessonId)
    .maybeSingle();
  if (findError && findError.code !== 'PGRST116') throw findError; // ignore "Results contain 0 rows" sentinel

  if (existing && existing.id) {
    const { data, error } = await supabase
      .from('academy_user_progress')
      .update({
        progress_percent: progressPercent,
        completed: progressPercent >= 100,
        last_accessed_at: now,
        updated_at: now,
        // ensure non-null fields are populated
        ...(courseId ? { course_id: courseId } : {}),
        ...(moduleId ? { module_id: moduleId } : {}),
      })
      .eq('id', existing.id)
      .select()
      .maybeSingle();
    if (error) throw error;
    return data;
  } else {
    try {
      const { data, error } = await supabase
        .from('academy_user_progress')
        .insert([{
          user_id: userId,
          course_id: courseId,
          module_id: moduleId,
          lesson_id: lessonId,
          progress_percent: progressPercent,
          completed: progressPercent >= 100,
          last_accessed_at: now,
          created_at: now,
          updated_at: now,
        }])
        .select()
        .maybeSingle();
      if (error) throw error;
      return data;
    } catch (e) {
      // Handle race: another insert won the race; update instead
      if (e && (e.code === '23505' || e.code === '409')) {
        const { data, error } = await supabase
          .from('academy_user_progress')
          .update({
            progress_percent: progressPercent,
            completed: progressPercent >= 100,
            last_accessed_at: now,
            updated_at: now,
          })
          .eq('user_id', userId)
          .eq('course_id', courseId)
          .eq('module_id', moduleId)
          .eq('lesson_id', lessonId)
          .select()
          .maybeSingle();
        if (error) throw error;
        return data;
      }
      throw e;
    }
  }
};
export const fetchLeaderboard = async (courseId) =>
  await supabase.from('academy_leaderboard').select('*').eq('course_id', courseId).order('points', { ascending: false });

// Peer Group Leaderboard (group vs. group)
export const fetchGroupLeaderboard = async (courseId) =>
  await supabase.from('academy_group_leaderboard').select('*').eq('course_id', courseId).order('total_points', { ascending: false });

// Streak Tracker
export const fetchStreak = async (userId, courseId) =>
  await supabase.from('academy_streaks').select('*').eq('user_id', userId).eq('course_id', courseId).maybeSingle();
export const updateStreak = async (userId, courseId, updates) =>
  await supabase.from('academy_streaks').update(updates).eq('user_id', userId).eq('course_id', courseId);

// Notes & Glossary
export const fetchNotes = async (userId, lessonId) =>
  await supabase.from('academy_user_notes').select('*').eq('user_id', userId).eq('lesson_id', lessonId);
export const upsertNote = async (note) =>
  await supabase.from('academy_user_notes').upsert([note]);
export const fetchGlossary = async (userId, courseId) =>
  await supabase.from('academy_user_glossary').select('*').eq('user_id', userId).eq('course_id', courseId);
export const upsertGlossary = async (entry) =>
  await supabase.from('academy_user_glossary').upsert([entry]);
export const deleteGlossaryEntry = async (id) =>
  await supabase.from('academy_user_glossary').delete().eq('id', id);

// AI Tutor (stub: replace with real backend call)
export const fetchAITutorResponse = async (input, context) => {
  // Example: Replace with your actual backend/AI function call
  // const { data, error } = await supabase.functions.invoke('ai-tutor', { body: { input, context } });
  // return { data, error };
  // Stubbed response for now:
  return {
    data: {
      response: `AI Feedback: "${input}"\n- Grammar: (corrected)\n- Pronunciation: (hint)\n- Suggestion: (next phrase)${context && context.topic ? `\nLet's practice more about ${context.topic}! (AI stub)` : ''}`,
      suggestion: 'Try saying: ...'
    },
    error: null
  };
};

// Adaptive Recommendations
export const fetchRecommendations = async (userId, courseId) =>
  await supabase.from('academy_adaptive_recommendations').select('*').eq('user_id', userId).eq('course_id', courseId);
export const acceptRecommendation = async (id, updates) =>
  await supabase.from('academy_adaptive_recommendations').update(updates).eq('id', id);
export const dismissRecommendation = async (id) =>
  await supabase.from('academy_adaptive_recommendations').delete().eq('id', id);

// Notifications
export const fetchNotifications = async (recipientId) =>
  await supabase.from('academy_notifications').select('*').eq('recipient_id', recipientId).order('created_at', { ascending: false });
export const markNotificationRead = async (id) =>
  await supabase.from('academy_notifications').update({ read: true }).eq('id', id);
export const deleteNotification = async (id) =>
  await supabase.from('academy_notifications').delete().eq('id', id);

// Admin Notifications (LMS panel)
export const fetchAdminNotifications = async () => {
  console.log('[DEBUG] Fetching admin notifications');
  
  try {
    // First try with explicit foreign key reference
    const { data, error } = await supabase
      .from('academy_notifications')
      .select(`
        *,
        profiles!fk_sender_profile(id, email, name, display_name)
      `)
      .order('created_at', { ascending: false });
      
    if (error) {
      console.log('[DEBUG] Join query error:', error);
      throw error;
    }
    
    return (data || []).map(n => ({
      ...n,
      sender_email: n.profiles?.email || n.sender_email || '',
      sender_display_name: n.profiles?.display_name || n.profiles?.name || n.sender_display_name || '',
    }));
  } catch (joinError) {
    console.log('[DEBUG] Falling back to simple query:', joinError);
    
    // Fallback to simple query without joins
    const { data, error } = await supabase
      .from('academy_notifications')
      .select('*')
      .order('created_at', { ascending: false });
      
    if (error) {
      console.log('[DEBUG] Simple query error:', error);
      throw error;
    }
    
    return data || [];
  }
};
export const sendNotification = async ({ message, title = '', type = 'admin', sender_id = null, sender_email = '', sender_display_name = '', target_role = 'all', recipient_id = null, link = null }) => {
  const { error } = await supabase
    .from('academy_notifications')
    .insert([
      {
        message,
        title,
        type,
        sender_id,
        sender_email,
        sender_display_name,
        target_role,
        recipient_id,
        link,
        created_at: new Date().toISOString(),
      },
    ]);
  if (error) throw error;
  return { success: !error, error };
};

// Calendar/Events
export const fetchEvents = async (courseId, groupId) => {
  let query = supabase.from('academy_calendar_events').select('*');
  if (courseId) query = query.eq('course_id', courseId);
  if (groupId) query = query.eq('group_id', groupId);
  return await query.order('start_time', { ascending: true });
};
export const createEvent = async (event) =>
  await supabase.from('academy_calendar_events').insert([event]);

// Surveys/Feedback
export const fetchSurveys = async (courseId) =>
  await supabase.from('academy_surveys').select('*').eq('course_id', courseId).eq('is_active', true);
export const fetchSurveyQuestions = async (surveyId) =>
  await supabase.from('academy_survey_questions').select('*').eq('survey_id', surveyId).order('order_index', { ascending: true });
export const submitSurveyResponse = async (response) =>
  await supabase.from('academy_survey_responses').insert([response]);

// Messaging
export const fetchThreads = async (userId) =>
  await supabase.from('academy_message_participants').select('thread_id').eq('user_id', userId);
export const fetchMessages = async (threadId) =>
  await supabase.from('academy_messages').select('*').eq('thread_id', threadId).order('sent_at', { ascending: true });
export const sendMessage = async (message) =>
  await supabase.from('academy_messages').insert([message]);

// Certificates, Peer Review, Plagiarism, Consents, API Keys
export const fetchCertificates = async (userId) =>
  await supabase.from('academy_certificates').select('*').eq('user_id', userId);

export const requestCertificate = async (userId) =>
  await supabase.from('academy_certificate_requests').insert([{ user_id: userId }]);
export const fetchPeerReviews = async (reviewerId) =>
  await supabase.from('academy_peer_reviews').select('*').eq('reviewer_id', reviewerId);

export const submitPeerReview = async (review) =>
  await supabase.from('academy_peer_reviews').insert([review]);
export const fetchPlagiarismReports = async (groupId) =>
  await supabase.from('academy_plagiarism_reports').select('*').eq('group_id', groupId);

export const submitPlagiarismReport = async (report) =>
  await supabase.from('academy_plagiarism_reports').insert([report]);
export const fetchConsents = async (userId) =>
  await supabase.from('academy_user_consents').select('*').eq('user_id', userId);

export const fetchUserConsents = async (userId) =>
  await supabase.from('academy_user_consents').select('*').eq('user_id', userId);

export const submitUserConsent = async (consent) =>
  await supabase.from('academy_user_consents').insert([consent]);
export const fetchApiKeys = async (userId) =>
  await supabase.from('academy_api_keys').select('*').eq('user_id', userId);

// Flashcards (CRUD & SRS)
export const fetchFlashcards = async (userId, courseId) =>
  await supabase.from('academy_flashcards').select('*').eq('user_id', userId).eq('course_id', courseId);
export const updateFlashcardSRS = async (flashcardId, updates) =>
  await supabase.from('academy_flashcards').update(updates).eq('id', flashcardId);
export const createFlashcard = async (flashcard) =>
  await supabase.from('academy_flashcards').insert([flashcard]);
export const deleteFlashcard = async (flashcardId) =>
  await supabase.from('academy_flashcards').delete().eq('id', flashcardId);

// Authentic Materials
export const fetchAuthenticMaterials = async (courseId) =>
  await supabase.from('academy_authentic_materials').select('*').eq('course_id', courseId).order('created_at', { ascending: false });
export const createAuthenticMaterial = async (material) =>
  await supabase.from('academy_authentic_materials').insert([material]);

// Notebook Tags (optional persistent tags)
export const fetchNotebookTags = async (userId, lessonId) =>
  await supabase.from('academy_notebook_tags').select('*').eq('user_id', userId).eq('lesson_id', lessonId);
export const addNotebookTag = async (tag) =>
  await supabase.from('academy_notebook_tags').insert([tag]);
export const deleteNotebookTag = async (tagId) =>
  await supabase.from('academy_notebook_tags').delete().eq('id', tagId);

// Learning Path
export const fetchLearningPath = async (userId, courseId) =>
  await supabase.from('academy_learning_paths').select('*').eq('user_id', userId).eq('course_id', courseId);
export const updateLearningPath = async (userId, courseId, path) =>
  await supabase.from('academy_learning_paths').upsert([{ user_id: userId, course_id: courseId, path }]);

// Dialogue Simulator
export const fetchDialoguePrompt = async (lessonId) =>
  await supabase.from('academy_dialogue_prompts').select('*').eq('lesson_id', lessonId);
export const submitDialogueResponse = async (response) =>
  await supabase.from('academy_dialogue_responses').insert([response]);
export const fetchDialogueFeedback = async (responseId) =>
  await supabase.from('academy_dialogue_feedback').select('*').eq('response_id', responseId);

// Pronunciation Practice
export const fetchPronunciationPhrases = async (courseId) =>
  await supabase.from('academy_pronunciation_phrases').select('*').eq('course_id', courseId);
export const submitPronunciationAttempt = async (attempt) =>
  await supabase.from('academy_pronunciation_attempts').insert([attempt]);
export const fetchPronunciationHistory = async (userId, courseId) =>
  await supabase.from('academy_pronunciation_attempts').select('*').eq('user_id', userId).eq('course_id', courseId);

// Scenario Simulator
export const fetchScenarios = async (courseId) =>
  await supabase.from('academy_scenarios').select('*').eq('course_id', courseId);
export const submitScenarioAttempt = async (attempt) =>
  await supabase.from('academy_scenario_attempts').insert([attempt]);
export const fetchScenarioHistory = async (userId, courseId) =>
  await supabase.from('academy_scenario_attempts').select('*').eq('user_id', userId).eq('course_id', courseId);

// Audio Player
export const fetchAudioTranscript = async (audioId) =>
  await supabase.from('academy_audio_transcripts').select('*').eq('audio_id', audioId);
export const submitAudioQuizResult = async (result) =>
  await supabase.from('academy_audio_quiz_results').insert([result]);
export const fetchAudioHistory = async (userId, audioId) =>
  await supabase.from('academy_audio_quiz_results').select('*').eq('user_id', userId).eq('audio_id', audioId);

// Video Player
export const fetchVideoTranscript = async (videoId) =>
  await supabase.from('academy_video_transcripts').select('*').eq('video_id', videoId);
export const submitVideoQuizResult = async (result) =>
  await supabase.from('academy_video_quiz_results').insert([result]);
export const fetchVideoHistory = async (userId, videoId) =>
  await supabase.from('academy_video_quiz_results').select('*').eq('user_id', userId).eq('video_id', videoId);

// Integrations
export const fetchIntegrations = async () => {
  const { data, error } = await supabase
    .from('academy_integrations')
    .select('*')
    .order('created_at', { ascending: false });
  return { data, error };
};

export const createIntegration = async (integration) => {
  const { data, error } = await supabase
    .from('academy_integrations')
    .insert([integration])
    .select()
    .maybeSingle();
  return { data, error };
};

export const updateIntegration = async (id, updates) => {
  const { data, error } = await supabase
    .from('academy_integrations')
    .update(updates)
    .eq('id', id)
    .select()
    .maybeSingle();
  return { data, error };
};

export const deleteIntegration = async (id) => {
  const { error } = await supabase
    .from('academy_integrations')
    .delete()
    .eq('id', id);
  return { success: !error, error };
};

// Academy Settings
export const fetchAcademySettings = async () => {
  // Always fetch the first (and only) row
  const { data, error } = await supabase
    .from('academy_settings')
    .select('*')
    .limit(1)
    .maybeSingle();
  return { data, error };
};

function cleanUuidField(obj, userId) {
  // Remove any existing updated_by property if not a valid UUID
  if (!userId || typeof userId !== 'string' || userId.trim() === '' || userId === 'null' || userId === null) {
    if ('updated_by' in obj) delete obj.updated_by;
  } else {
    obj.updated_by = userId;
  }
  return obj;
}

export const updateAcademySettings = async (settings, userId) => {
  // Ensure a row exists
  const { id } = await ensureSettingsRow('academy_settings', { settings: {}, created_at: new Date().toISOString() });
  const updateObj = { settings };
  cleanUuidField(updateObj, userId);
  const { data, error } = await supabase
    .from('academy_settings')
    .update(updateObj)
    .eq('id', id)
    .select()
    .maybeSingle();
  return { data, error };
};

// Settings by key (for key-value settings table)
export const fetchSettingByKey = async (key) => {
  const { data, error } = await supabase
    .from('academy_settings')
    .select('value')
    .eq('key', key)
    .maybeSingle();
  return { data: data?.value, error };
};

export const upsertSettingByKey = async (key, value, userId) => {
  const { data, error } = await supabase
    .from('academy_settings')
    .upsert([{ key, value, updated_by: userId }], { onConflict: ['key'] })
    .select()
    .maybeSingle();
  return { data, error };
};

// Branding
export const fetchBranding = async () => {
  const { data, error } = await supabase
    .from('academy_branding')
    .select('*')
    .limit(1)
    .maybeSingle();
  return { data, error };
};
export const updateBranding = async (branding, userId) => {
  // Ensure a row exists
  const { id } = await ensureSettingsRow('academy_branding', { color_scheme: '', updated_at: new Date().toISOString() });
  const brandingUpdate = { ...branding };
  if (brandingUpdate.colorScheme !== undefined) {
    brandingUpdate.color_scheme = brandingUpdate.colorScheme;
    delete brandingUpdate.colorScheme;
  }
  if (brandingUpdate.colorscheme !== undefined) {
    brandingUpdate.color_scheme = brandingUpdate.colorscheme;
    delete brandingUpdate.colorscheme;
  }
  const updateObj = {
    ...brandingUpdate,
    updated_at: new Date().toISOString(),
  };
  cleanUuidField(updateObj, userId);
  const { data, error } = await supabase
    .from('academy_branding')
    .update(updateObj)
    .eq('id', id)
    .select()
    .maybeSingle();
  return { data, error };
};
// Maintenance
export const fetchMaintenance = async () => {
  const { data, error } = await supabase
    .from('academy_maintenance')
    .select('*')
    .limit(1)
    .maybeSingle();
  return { data, error };
};
export const updateMaintenance = async (maintenance, userId) => {
  // Ensure a row exists
  const { id } = await ensureSettingsRow('academy_maintenance', { maintenance: false, support_email: '', updated_at: new Date().toISOString() });
  const updateObj = {
    ...maintenance,
    updated_at: new Date().toISOString(),
  };
  cleanUuidField(updateObj, userId);
  const { data, error } = await supabase
    .from('academy_maintenance')
    .update(updateObj)
    .eq('id', id)
    .select()
    .maybeSingle();
  return { data, error };
};
// Security
export const fetchSecuritySettings = async () => {
  const { data, error } = await supabase
    .from('academy_security_settings')
    .select('*')
    .limit(1)
    .maybeSingle();
  return { data, error };
};
export const updateSecuritySettings = async (security, userId) => {
  // Ensure a row exists
  const { id } = await ensureSettingsRow('academy_security_settings', { updated_at: new Date().toISOString() });
  const updateObj = {
    ...security,
    updated_at: new Date().toISOString(),
  };
  cleanUuidField(updateObj, userId);
  const { data, error } = await supabase
    .from('academy_security_settings')
    .update(updateObj)
    .eq('id', id)
    .select()
    .maybeSingle();
  return { data, error };
};
// Data Privacy
export const fetchDataPrivacy = async () => {
  const { data, error } = await supabase
    .from('academy_data_privacy')
    .select('*')
    .limit(1)
    .maybeSingle();
  return { data, error };
};
export const updateDataPrivacy = async (privacy, userId) => {
  // Ensure a row exists
  const { id } = await ensureSettingsRow('academy_data_privacy', { updated_at: new Date().toISOString() });
  const updateObj = {
    ...privacy,
    updated_at: new Date().toISOString(),
  };
  cleanUuidField(updateObj, userId);
  const { data, error } = await supabase
    .from('academy_data_privacy')
    .update(updateObj)
    .eq('id', id)
    .select()
    .maybeSingle();
  return { data, error };
};
// Analytics
export const fetchAnalyticsSettings = async () => {
  const { data, error } = await supabase
    .from('academy_analytics_settings')
    .select('*')
    .limit(1)
    .maybeSingle();
  return { data, error };
};
export const updateAnalyticsSettings = async (analytics, userId) => {
  // Ensure a row exists
  const { id } = await ensureSettingsRow('academy_analytics_settings', { updated_at: new Date().toISOString() });
  const updateObj = {
    ...analytics,
    updated_at: new Date().toISOString(),
  };
  cleanUuidField(updateObj, userId);
  const { data, error } = await supabase
    .from('academy_analytics_settings')
    .update(updateObj)
    .eq('id', id)
    .select()
    .maybeSingle();
  return { data, error };
};
// API Keys
export const fetchApiKeysForIntegration = async (integrationId) => {
  const { data, error } = await supabase
    .from('academy_api_keys')
    .select('*')
    .eq('integration_id', integrationId);
  return { data, error };
};
export const createApiKey = async ({ user_id, integration_id, api_key, label, expires_at }) => {
  const { data, error } = await supabase
    .from('academy_api_keys')
    .insert([{ user_id, integration_id, api_key, label, expires_at }])
    .select()
    .maybeSingle();
  return { data, error };
};

// Upload a file to the branding-assets bucket and return the public URL
export const uploadBrandingLogo = async (file, userId) => {
  const fileExt = file.name.split('.').pop();
  const fileName = `logo_${userId}_${Date.now()}.${fileExt}`;
  const filePath = `${fileName}`;
  // Upload to bucket
  const { error: uploadError } = await supabase.storage
    .from('branding-assets')
    .upload(filePath, file, { upsert: true, contentType: file.type });
  if (uploadError) return { error: uploadError };
  // Get public URL
  const { data } = supabase.storage.from('branding-assets').getPublicUrl(filePath);
  return { url: data.publicUrl };
};

// Helper: Ensure a row exists in a settings table, insert default if missing
async function ensureSettingsRow(table, defaultRow = {}) {
  const { data: row, error } = await supabase
    .from(table)
    .select('id')
    .limit(1)
    .maybeSingle();
  if (row && row.id) return { id: row.id };
  // Insert default row
  const { data: inserted, error: insertError } = await supabase
    .from(table)
    .insert([{ ...defaultRow }])
    .select('id')
    .maybeSingle();
  if (insertError || !inserted?.id) throw insertError || new Error(`Failed to insert default row for ${table}`);
  return { id: inserted.id };
}

// System Dashboard Fetchers
export const fetchSystemStatus = async () => {
  const { data, error } = await supabase
    .from('academy_system_status')
    .select('*')
    .order('checked_at', { ascending: false })
    .limit(1)
    .maybeSingle();
  return { data, error };
};

export const fetchSystemAlerts = async () => {
  const { data, error } = await supabase
    .from('academy_system_alerts')
    .select('*')
    .order('created_at', { ascending: false });
  return { data, error };
};

export const fetchStorageUsage = async () => {
  const { data, error } = await supabase
    .from('academy_storage_usage')
    .select('*')
    .order('checked_at', { ascending: false });
  return { data, error };
};

export const fetchApiUsage = async () => {
  const { data, error } = await supabase
    .from('academy_api_usage')
    .select('*')
    .order('period_end', { ascending: false });
  return { data, error };
};

export const fetchSystemLogs = async () => {
  const { data, error } = await supabase
    .from('academy_system_logs')
    .select('*')
    .order('created_at', { ascending: false })
    .limit(50);
  return { data, error };
};
// Role Templates (CRUD)
export const fetchRoleTemplates = async () => {
  const { data, error } = await supabase
    .from('academy_role_templates')
    .select('*')
    .order('created_at', { ascending: false });
  return { data, error };
};

export const createRoleTemplate = async (template, userId) => {
  const insertObj = { ...template, created_at: new Date().toISOString() };
  cleanUuidField(insertObj, userId);
  const { data, error } = await supabase
    .from('academy_role_templates')
    .insert([insertObj])
    .select()
    .maybeSingle();
  return { data, error };
};

export const updateRoleTemplate = async (id, updates, userId) => {
  const updateObj = { ...updates, updated_at: new Date().toISOString() };
  cleanUuidField(updateObj, userId);
  const { data, error } = await supabase
    .from('academy_role_templates')
    .update(updateObj)
    .eq('id', id)
    .select()
    .maybeSingle();
  return { data, error };
};

export const deleteRoleTemplate = async (id) => {
  const { error } = await supabase
    .from('academy_role_templates')
    .delete()
    .eq('id', id);
  return { success: !error, error };
};

// Optionally: Apply a template to a role (copy permissions/settings from template to role)
export const applyRoleTemplateToRole = async (templateId, roleId, userId) => {
  // Fetch template
  const { data: template, error: fetchError } = await supabase
    .from('academy_role_templates')
    .select('*')
    .eq('id', templateId)
    .maybeSingle();
  if (fetchError || !template) return { error: fetchError || 'Template not found' };
  // Update role with template's permissions/settings
  const updateObj = {
    ...('permissions' in template ? { permissions: template.permissions } : {}),
    ...('description' in template ? { description: template.description } : {}),
    updated_at: new Date().toISOString(),
  };
  cleanUuidField(updateObj, userId);
  const { data, error } = await supabase
    .from('academy_roles')
    .update(updateObj)
    .eq('id', roleId)
    .select()
    .maybeSingle();
  return { data, error };
};

// Permission Groups (CRUD)
export const fetchPermissionGroups = async () => {
  const { data, error } = await supabase
    .from('academy_permission_groups')
    .select('*')
    .order('name', { ascending: true });
  return { data, error };
};

export const createPermissionGroup = async (group, userId) => {
  const insertObj = { ...group, created_at: new Date().toISOString() };
  cleanUuidField(insertObj, userId);
  const { data, error } = await supabase
    .from('academy_permission_groups')
    .insert([insertObj])
    .select()
    .maybeSingle();
  return { data, error };
};

export const updatePermissionGroup = async (id, updates, userId) => {
  const updateObj = { ...updates, updated_at: new Date().toISOString() };
  cleanUuidField(updateObj, userId);
  const { data, error } = await supabase
    .from('academy_permission_groups')
    .update(updateObj)
    .eq('id', id)
    .select()
    .maybeSingle();
  return { data, error };
};

export const deletePermissionGroup = async (id) => {
  const { error } = await supabase
    .from('academy_permission_groups')
    .delete()
    .eq('id', id);
  return { success: !error, error };
};

// Role <-> Permission Group Assignment
export const fetchRolePermissionGroups = async (role_id) => {
  const { data, error } = await supabase
    .from('academy_role_permission_groups')
    .select('*, permission_group:permission_group_id(*)')
    .eq('role_id', role_id);
  return { data, error };
};

export const assignPermissionGroupToRole = async (role_id, permission_group_id, userId) => {
  const insertObj = {
    role_id,
    permission_group_id,
    assigned_at: new Date().toISOString(),
    assigned_by: userId
  };
  const { data, error } = await supabase
    .from('academy_role_permission_groups')
    .insert([insertObj])
    .select()
    .maybeSingle();
  return { data, error };
};

export const unassignPermissionGroupFromRole = async (role_id, permission_group_id) => {
  const { error } = await supabase
    .from('academy_role_permission_groups')
    .delete()
    .eq('role_id', role_id)
    .eq('permission_group_id', permission_group_id);
  return { success: !error, error };
};

// Audit Logs (CRUD/fetch)
export const fetchRoleAuditLogs = async (userId = null, limit = 50) => {
  let query = supabase
    .from('academy_role_audit_logs')
    .select('*')
    .order('created_at', { ascending: false })
    .limit(limit);
  if (userId) query = query.eq('user_id', userId);
  const { data, error } = await query;
  return { data, error };
};

export const logRoleAudit = async ({ action, user_id, performed_by, details }) => {
  const insertObj = {
    action,
    user_id,
    performed_by,
    details,
    created_at: new Date().toISOString()
  };
  const { data, error } = await supabase
    .from('academy_role_audit_logs')
    .insert([insertObj])
    .select()
    .maybeSingle();
  return { data, error };
};

// Fetch users from 'profiles' with advanced filters
export const fetchUsers = async (filters = {}, limit = 50, offset = 0) => {
  let query = supabase.from('profiles').select('*');
  // Apply filters if present
  if (filters.email) query = query.ilike('email', `%${filters.email}%`);
  if (filters.name) query = query.ilike('name', `%${filters.name}%`);
  if (filters.display_name) query = query.ilike('display_name', `%${filters.display_name}%`);
  if (filters.location) query = query.ilike('location', `%${filters.location}%`);
  if (filters.occupation) query = query.ilike('occupation', `%${filters.occupation}%`);
  if (filters.level) query = query.eq('level', filters.level);
  if (filters.gender) query = query.eq('gender', filters.gender);
  if (filters.native_languages && filters.native_languages.length > 0) {
    // JSONB array contains any
    for (const lang of filters.native_languages) {
      query = query.contains('native_languages', [lang]);
    }
  }
  if (filters.target_languages && filters.target_languages.length > 0) {
    for (const lang of filters.target_languages) {
      query = query.contains('target_languages', [lang]);
    }
  }
  if (filters.age_min !== undefined) query = query.gte('age', filters.age_min);
  if (filters.age_max !== undefined) query = query.lte('age', filters.age_max);
  if (filters.last_active_after) query = query.gte('last_active', filters.last_active_after);
  if (filters.last_active_before) query = query.lte('last_active', filters.last_active_before);
  query = query.range(offset, offset + limit - 1);
  const { data, error } = await query;
  return { data, error };
};

// Assign a role to a user (add to academy_user_roles)
export const assignRoleToUser = async (user_id, role_id, assigned_by) => {
  const insertObj = {
    user_id,
    role_id,
    assigned_at: new Date().toISOString(),
    assigned_by
  };
  const { data, error } = await supabase
    .from('academy_user_roles')
    .insert([insertObj])
    .select()
    .maybeSingle();
  return { data, error };
};

// Remove a role from a user
export const removeRoleFromUser = async (user_id, role_id) => {
  const { error } = await supabase
    .from('academy_user_roles')
    .delete()
    .eq('user_id', user_id)
    .eq('role_id', role_id);
  return { success: !error, error };
};

// Roles (CRUD)
export const fetchRoles = async () => {
  const { data, error } = await supabase.from('academy_roles').select('*').order('created_at', { ascending: true });
  if (error) throw error;
  return data;
};

export const createRole = async (role, userId) => {
  const { data, error } = await supabase
    .from('academy_roles')
    .insert([{ ...role, created_by: userId, updated_by: userId }])
    .select()
    .single();
  if (error) throw error;
  return data;
};

export const updateRole = async (id, updates, userId) => {
  const { data, error } = await supabase
    .from('academy_roles')
    .update({ ...updates, updated_by: userId, updated_at: new Date().toISOString() })
    .eq('id', id)
    .select()
    .single();
  if (error) throw error;
  return data;
};

export const deleteRole = async (id) => {
  const { error } = await supabase.from('academy_roles').delete().eq('id', id);
  if (error) throw error;
  return { success: true };
};

// Permissions (CRUD)
export const fetchPermissions = async () => {
  const { data, error } = await supabase.from('academy_permissions').select('*').order('permission', { ascending: true });
  if (error) throw error;
  return data;
};

export const createPermission = async (permission, userId) => {
  const { data, error } = await supabase
    .from('academy_permissions')
    .insert([{ ...permission, created_by: userId }])
    .select()
    .single();
  if (error) throw error;
  return data;
};

export const updatePermission = async (id, updates, userId) => {
  const { data, error } = await supabase
    .from('academy_permissions')
    .update({ ...updates, updated_by: userId, updated_at: new Date().toISOString() })
    .eq('id', id)
    .select()
    .single();
  if (error) throw error;
  return data;
};

export const deletePermission = async (id) => {
  const { error } = await supabase.from('academy_permissions').delete().eq('id', id);
  if (error) throw error;
  return { success: true };
};
